import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as s from "../InputPage.styles";
import { styled } from "styled-components";
import { AnalysisForm } from "../machine";
import AccessoryPickerModal from "../TractorInfo/AccessoryPickerModal";
import { useFormattedNumberField } from "../LandInfo/useFormattedNumberField";

interface Props {
  formData: AnalysisForm;
  setFormData: Dispatch<SetStateAction<AnalysisForm>>;
}

function TaskSection({ formData, setFormData }: Props) {
  const [openAccessoryPicker, setOpenAccessoryPicker] = useState(false);

  const rotary = formData.implementList.find(
    (i) => i.implement === "로터리/써래"
  );
  const plow = formData.implementList.find((i) => i.implement === "쟁기");
  const loader = formData.implementList.find((i) => i.implement === "로더");
  const spreader = formData.implementList.find((i) => i.implement === "살포기");

  const updateImplement = (
    implementName: string,
    updates: Partial<AnalysisForm["implementList"][number]>
  ) => {
    setFormData((prev) => ({
      ...prev,
      implementList: prev.implementList.map((impl) =>
        impl.implement === implementName ? { ...impl, ...updates } : impl
      ),
    }));
  };

  type ImplementName = "로터리/써래" | "쟁기" | "로더" | "살포기";
  type ImplementField = "periodDays" | "speed" | "width";

  function useImplementNumberField(
    formData: AnalysisForm,
    updateImplement: (
      implement: ImplementName,
      updates: Partial<AnalysisForm["implementList"][number]>
    ) => void,
    implement: ImplementName,
    field: ImplementField,
    options: { min?: number; max?: number; defaultBase?: number } = {}
  ) {
    const impl = formData.implementList.find((i) => i.implement === implement);
    const baseValue =
      (impl?.[field] as number | undefined) ?? options.defaultBase ?? 0;

    return useFormattedNumberField({
      baseValue,
      min: options.min,
      max: options.max,
      isPrice: false,
      onChangeBaseValue: (value) => {
        updateImplement(implement, { [field]: value } as any);
      },
    });
  }

  // 로터리/써래
  const rotaryDays = useImplementNumberField(
    formData,
    updateImplement,
    "로터리/써래",
    "periodDays",
    { min: 1, max: 30 }
  );

  // 로터리/써래 - 속도
  const rotarySpeed = useImplementNumberField(
    formData,
    updateImplement,
    "로터리/써래",
    "speed"
  );

  // 로터리/써래 - 사용폭
  const rotaryWidth = useImplementNumberField(
    formData,
    updateImplement,
    "로터리/써래",
    "width"
  );

  // 쟁기
  const plowDays = useImplementNumberField(
    formData,
    updateImplement,
    "쟁기",
    "periodDays",
    { min: 1, max: 30 }
  );

  // 쟁기 - 속도
  const plowSpeed = useImplementNumberField(
    formData,
    updateImplement,
    "쟁기",
    "speed"
  );

  // 쟁기 - 사용폭
  const plowWidth = useImplementNumberField(
    formData,
    updateImplement,
    "쟁기",
    "width"
  );

  // 로더
  const loaderDays = useImplementNumberField(
    formData,
    updateImplement,
    "로더",
    "periodDays",
    { min: 1, max: 30 }
  );

  // 로더 - 속도
  const loaderSpeed = useImplementNumberField(
    formData,
    updateImplement,
    "로더",
    "speed"
  );

  // 로더 - 사용폭
  const loaderWidth = useImplementNumberField(
    formData,
    updateImplement,
    "로더",
    "width"
  );

  // 살포기
  const spreaderDays = useImplementNumberField(
    formData,
    updateImplement,
    "살포기",
    "periodDays",
    { min: 1, max: 30 }
  );

  // 살포기 - 속도
  const spreaderSpeed = useImplementNumberField(
    formData,
    updateImplement,
    "살포기",
    "speed"
  );

  // 살포기 - 사용폭
  const spreaderWidth = useImplementNumberField(
    formData,
    updateImplement,
    "살포기",
    "width"
  );

  return (
    <s.Section>
      <s.SectionTitleContainer>
        <s.SectionTitle>1. 농작업 입력</s.SectionTitle>
      </s.SectionTitleContainer>

      {rotary ? (
        <>
          <s.SectionInner>
            <AccessoryHeader>
              <HeaderName>경운</HeaderName>
              <AccessoryName onClick={() => setOpenAccessoryPicker(true)}>
                로터리/써래
                <div className="count">{rotary.units}대</div>
                <img
                  src="/asset/images/arrow/ArrowBottom24-light-gray.svg"
                  alt=""
                />
              </AccessoryName>
            </AccessoryHeader>
          </s.SectionInner>
          <s.SectionInner $top={8}>
            <s.Label>
              <s.LabelTitle>작업일수</s.LabelTitle>
            </s.Label>
            <s.Stepper>
              <s.StepTextButton
                $bg="#F1F1FE"
                $color="#4B47FC"
                onClick={() =>
                  updateImplement("로터리/써래", {
                    periodDays: Math.max(0, (rotary.periodDays ?? 0) - 1),
                  })
                }
              >
                -1일
              </s.StepTextButton>
              <s.StepperInner>
                <s.StepperInput
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9.]*"
                  value={rotaryDays.display}
                  onChange={(e) => rotaryDays.handleChange(e.target.value)}
                  onBlur={rotaryDays.handleBlur}
                />

                <s.StepperUnit>일</s.StepperUnit>
              </s.StepperInner>
              <s.StepTextButton
                $bg="#FFF6F6"
                $color="#EC4F4F"
                onClick={() =>
                  updateImplement("로터리/써래", {
                    periodDays: Math.min((rotary.periodDays ?? 0) + 1, 30),
                  })
                }
              >
                +1일
              </s.StepTextButton>
            </s.Stepper>
          </s.SectionInner>
          <s.SectionInner $top={8}>
            <InnerRow>
              <InnerRowItem>
                <s.Label>
                  <s.LabelTitle>속도</s.LabelTitle>
                </s.Label>
                <s.InputWrapper>
                  <s.Input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9.]*"
                    value={rotarySpeed.display}
                    onChange={(e) => rotarySpeed.handleChange(e.target.value)}
                    onBlur={rotarySpeed.handleBlur}
                  />
                  <span>km/h</span>
                </s.InputWrapper>
              </InnerRowItem>
              <InnerRowItem>
                <s.Label>
                  <s.LabelTitle>사용폭</s.LabelTitle>
                </s.Label>
                <s.InputWrapper>
                  <s.Input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9.]*"
                    value={rotaryWidth.display}
                    onChange={(e) => rotaryWidth.handleChange(e.target.value)}
                    onBlur={rotaryWidth.handleBlur}
                  />
                  <span>m</span>
                </s.InputWrapper>
              </InnerRowItem>
            </InnerRow>
          </s.SectionInner>
        </>
      ) : (
        <>
          <s.SectionInner>
            <AccessoryHeader>
              <HeaderName>경운</HeaderName>
              <AccessoryName onClick={() => setOpenAccessoryPicker(true)}>
                로터리/써래
                <div className="count">없음</div>
                <img
                  src="/asset/images/arrow/ArrowBottom24-light-gray.svg"
                  alt=""
                />
              </AccessoryName>
            </AccessoryHeader>
          </s.SectionInner>
          <s.SectionInner $top={8}>
            <s.AddButton onClick={() => setOpenAccessoryPicker(true)}>
              <img src="/asset/images/icon/plus.svg" alt="" />
              <span>보유 중인 로터리/써래를 추가해주세요</span>
            </s.AddButton>
          </s.SectionInner>
        </>
      )}

      {plow ? (
        <>
          <s.SectionInner>
            <AccessoryHeader>
              <HeaderName>정지</HeaderName>
              <AccessoryName onClick={() => setOpenAccessoryPicker(true)}>
                쟁기
                <div className="count">{plow.units}대</div>
                <img
                  src="/asset/images/arrow/ArrowBottom24-light-gray.svg"
                  alt=""
                />
              </AccessoryName>
            </AccessoryHeader>
          </s.SectionInner>
          <s.SectionInner $top={8}>
            <s.Label>
              <s.LabelTitle>작업일수</s.LabelTitle>
            </s.Label>
            <s.Stepper>
              <s.StepTextButton
                $bg="#F1F1FE"
                $color="#4B47FC"
                onClick={() =>
                  updateImplement("쟁기", {
                    periodDays: Math.max(0, (plow.periodDays ?? 0) - 1),
                  })
                }
              >
                -1일
              </s.StepTextButton>
              <s.StepperInner>
                <s.StepperInput
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9.]*"
                  value={plowDays.display}
                  onChange={(e) => plowDays.handleChange(e.target.value)}
                  onBlur={plowDays.handleBlur}
                />

                <s.StepperUnit>일</s.StepperUnit>
              </s.StepperInner>
              <s.StepTextButton
                $bg="#FFF6F6"
                $color="#EC4F4F"
                onClick={() =>
                  updateImplement("쟁기", {
                    periodDays: Math.min((plow.periodDays ?? 0) + 1, 30),
                  })
                }
              >
                +1일
              </s.StepTextButton>
            </s.Stepper>
          </s.SectionInner>
          <s.SectionInner $top={8}>
            <InnerRow>
              <InnerRowItem>
                <s.Label>
                  <s.LabelTitle>속도</s.LabelTitle>
                </s.Label>
                <s.InputWrapper>
                  <s.Input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9.]*"
                    value={plowSpeed.display}
                    onChange={(e) => plowSpeed.handleChange(e.target.value)}
                    onBlur={plowSpeed.handleBlur}
                  />
                  <span>km/h</span>
                </s.InputWrapper>
              </InnerRowItem>
              <InnerRowItem>
                <s.Label>
                  <s.LabelTitle>사용폭</s.LabelTitle>
                </s.Label>
                <s.InputWrapper>
                  <s.Input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9.]*"
                    value={plowWidth.display}
                    onChange={(e) => plowWidth.handleChange(e.target.value)}
                    onBlur={plowWidth.handleBlur}
                  />
                  <span>m</span>
                </s.InputWrapper>
              </InnerRowItem>
            </InnerRow>
          </s.SectionInner>
        </>
      ) : (
        <>
          <s.SectionInner>
            <AccessoryHeader>
              <HeaderName>정지</HeaderName>
              <AccessoryName onClick={() => setOpenAccessoryPicker(true)}>
                쟁기
                <div className="count">없음</div>
                <img
                  src="/asset/images/arrow/ArrowBottom24-light-gray.svg"
                  alt=""
                />
              </AccessoryName>
            </AccessoryHeader>
          </s.SectionInner>
          <s.SectionInner $top={8}>
            <s.AddButton onClick={() => setOpenAccessoryPicker(true)}>
              <img src="/asset/images/icon/plus.svg" alt="" />
              <span>보유 중인 쟁기를 추가해주세요</span>
            </s.AddButton>
          </s.SectionInner>
        </>
      )}

      {loader ? (
        <>
          <s.SectionInner>
            <AccessoryHeader>
              <HeaderName>하역/운반</HeaderName>
              <AccessoryName onClick={() => setOpenAccessoryPicker(true)}>
                로더
                <div className="count">{loader.units}대</div>
                <img
                  src="/asset/images/arrow/ArrowBottom24-light-gray.svg"
                  alt=""
                />
              </AccessoryName>
            </AccessoryHeader>
          </s.SectionInner>
          <s.SectionInner $top={8}>
            <s.Label>
              <s.LabelTitle>작업일수</s.LabelTitle>
            </s.Label>
            <s.Stepper>
              <s.StepTextButton
                $bg="#F1F1FE"
                $color="#4B47FC"
                onClick={() =>
                  updateImplement("로더", {
                    periodDays: Math.max(0, (loader.periodDays ?? 0) - 1),
                  })
                }
              >
                -1일
              </s.StepTextButton>
              <s.StepperInner>
                <s.StepperInput
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9.]*"
                  value={loaderDays.display}
                  onChange={(e) => loaderDays.handleChange(e.target.value)}
                  onBlur={loaderDays.handleBlur}
                />

                <s.StepperUnit>일</s.StepperUnit>
              </s.StepperInner>
              <s.StepTextButton
                $bg="#FFF6F6"
                $color="#EC4F4F"
                onClick={() =>
                  updateImplement("로더", {
                    periodDays: Math.min((loader.periodDays ?? 0) + 1, 30),
                  })
                }
              >
                +1일
              </s.StepTextButton>
            </s.Stepper>
          </s.SectionInner>
          <s.SectionInner $top={8}>
            <InnerRow>
              <InnerRowItem>
                <s.Label>
                  <s.LabelTitle>속도</s.LabelTitle>
                </s.Label>
                <s.InputWrapper>
                  <s.Input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9.]*"
                    value={loaderSpeed.display}
                    onChange={(e) => loaderSpeed.handleChange(e.target.value)}
                    onBlur={loaderSpeed.handleBlur}
                  />
                  <span>km/h</span>
                </s.InputWrapper>
              </InnerRowItem>
              <InnerRowItem>
                <s.Label>
                  <s.LabelTitle>사용폭</s.LabelTitle>
                </s.Label>
                <s.InputWrapper>
                  <s.Input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9.]*"
                    value={loaderWidth.display}
                    onChange={(e) => loaderWidth.handleChange(e.target.value)}
                    onBlur={loaderWidth.handleBlur}
                  />
                  <span>m</span>
                </s.InputWrapper>
              </InnerRowItem>
            </InnerRow>
          </s.SectionInner>
        </>
      ) : (
        <>
          <s.SectionInner>
            <AccessoryHeader>
              <HeaderName>하역/운반</HeaderName>
              <AccessoryName onClick={() => setOpenAccessoryPicker(true)}>
                로더
                <div className="count">없음</div>
                <img
                  src="/asset/images/arrow/ArrowBottom24-light-gray.svg"
                  alt=""
                />
              </AccessoryName>
            </AccessoryHeader>
          </s.SectionInner>
          <s.SectionInner $top={8}>
            <s.AddButton onClick={() => setOpenAccessoryPicker(true)}>
              <img src="/asset/images/icon/plus.svg" alt="" />
              <span>보유 중인 로더를 추가해주세요</span>
            </s.AddButton>
          </s.SectionInner>
        </>
      )}

      {spreader ? (
        <>
          <s.SectionInner>
            <AccessoryHeader>
              <HeaderName>시비/방제</HeaderName>
              <AccessoryName onClick={() => setOpenAccessoryPicker(true)}>
                살포기
                <div className="count">{spreader.units}대</div>
                <img
                  src="/asset/images/arrow/ArrowBottom24-light-gray.svg"
                  alt=""
                />
              </AccessoryName>
            </AccessoryHeader>
          </s.SectionInner>
          <s.SectionInner $top={8}>
            <s.Label>
              <s.LabelTitle>작업일수</s.LabelTitle>
            </s.Label>
            <s.Stepper>
              <s.StepTextButton
                $bg="#F1F1FE"
                $color="#4B47FC"
                onClick={() =>
                  updateImplement("살포기", {
                    periodDays: Math.max(0, (spreader.periodDays ?? 0) - 1),
                  })
                }
              >
                -1일
              </s.StepTextButton>
              <s.StepperInner>
                <s.StepperInput
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9.]*"
                  value={spreaderDays.display}
                  onChange={(e) => spreaderDays.handleChange(e.target.value)}
                  onBlur={spreaderDays.handleBlur}
                />

                <s.StepperUnit>일</s.StepperUnit>
              </s.StepperInner>
              <s.StepTextButton
                $bg="#FFF6F6"
                $color="#EC4F4F"
                onClick={() =>
                  updateImplement("살포기", {
                    periodDays: Math.min((spreader.periodDays ?? 0) + 1, 30),
                  })
                }
              >
                +1일
              </s.StepTextButton>
            </s.Stepper>
          </s.SectionInner>
          <s.SectionInner $top={8}>
            <InnerRow>
              <InnerRowItem>
                <s.Label>
                  <s.LabelTitle>속도</s.LabelTitle>
                </s.Label>
                <s.InputWrapper>
                  <s.Input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9.]*"
                    value={spreaderSpeed.display}
                    onChange={(e) => spreaderSpeed.handleChange(e.target.value)}
                    onBlur={spreaderSpeed.handleBlur}
                  />
                  <span>km/h</span>
                </s.InputWrapper>
              </InnerRowItem>
              <InnerRowItem>
                <s.Label>
                  <s.LabelTitle>사용폭</s.LabelTitle>
                </s.Label>
                <s.InputWrapper>
                  <s.Input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9.]*"
                    value={spreaderWidth.display}
                    onChange={(e) => spreaderWidth.handleChange(e.target.value)}
                    onBlur={spreaderWidth.handleBlur}
                  />
                  <span>m</span>
                </s.InputWrapper>
              </InnerRowItem>
            </InnerRow>
          </s.SectionInner>
        </>
      ) : (
        <>
          <s.SectionInner>
            <AccessoryHeader>
              <HeaderName>시비/방제</HeaderName>
              <AccessoryName onClick={() => setOpenAccessoryPicker(true)}>
                살포기
                <div className="count">없음</div>
                <img
                  src="/asset/images/arrow/ArrowBottom24-light-gray.svg"
                  alt=""
                />
              </AccessoryName>
            </AccessoryHeader>
          </s.SectionInner>
          <s.SectionInner $top={8}>
            <s.AddButton onClick={() => setOpenAccessoryPicker(true)}>
              <img src="/asset/images/icon/plus.svg" alt="" />
              <span>보유 중인 살포기를 추가해주세요</span>
            </s.AddButton>
          </s.SectionInner>
        </>
      )}

      {openAccessoryPicker && (
        <AccessoryPickerModal
          formData={formData}
          setFormData={setFormData}
          isEdit
          onClose={() => setOpenAccessoryPicker(false)}
        />
      )}
    </s.Section>
  );
}

export default TaskSection;

const AccessoryHeader = styled.div`
  padding: 8px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  border-bottom: 1px solid
    var(--Alias-Color-Line-Outline, rgba(23, 30, 38, 0.1));
`;

const HeaderName = styled.div`
  flex: 1 0 0;
  color: rgba(23, 30, 38, 0.78);
  font-size: 16px;
  font-weight: 700;
  line-height: 150%; /* 24px */
`;

const AccessoryName = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;

  color: #171e26;
  font-size: 16px;
  font-weight: 500;
  line-height: 150%; /* 24px */

  & > .count {
    font-weight: 700;
  }

  & > img {
    transform: rotate(-90deg);
  }
`;

const InnerRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const InnerRowItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1 0 0;
  min-width: 0;
`;
